import * as Sentry from '@sentry/react';
import { firebaseAnalytics } from './firebase-analytics';
import { initializeFirebaseAnalytics } from './firebase';
import { 
  createSafeLogData, 
  sanitizeLogMessage, 
  createAuthLogData, 
  createApiLogData 
} from '../utils/pii-masker';
import { SessionExpiredError, isSessionExpiredError } from '../utils/errors';

// Declarações globais para configurações injetadas pelo CI/CD
declare global {
  interface Window {
    __SENTRY_DSN__?: string;
    __APP_CONFIG__?: {
      sentryDsn?: string;
      environment?: string;
      buildTime?: string;
      gitSha?: string;
    };
    __RUNTIME_CONFIG__?: {
      sentry?: {
        dsn?: string;
        environment?: string;
      };
      firebase?: {
        apiKey?: string;
        authDomain?: string;
        projectId?: string;
        storageBucket?: string;
        messagingSenderId?: string;
        appId?: string;
        measurementId?: string;
      };
      api?: {
        baseUrl?: string;
        timeout?: number;
      };
      features?: {
        analytics?: boolean;
        monitoring?: boolean;
        debugging?: boolean;
      };
    };
  }
}

// Configuração do ambiente
const getEnvironment = (): 'production' | 'staging' | 'development' => {
  // 1. Development (local)
  if (process.env.NODE_ENV === 'development') {
    return 'development';
  }
  
  // 2. Staging (URL contém staging OU configuração injetada)
  if (typeof window !== 'undefined') {
    const isStaging = window.location.hostname.includes('staging') || 
                     (window as any).__APP_CONFIG__?.environment === 'staging' ||
                     (window as any).__RUNTIME_CONFIG__?.sentry?.environment === 'staging';
    
    if (isStaging) {
      return 'staging';
    }
  }
  
  // 3. Production (padrão)
  return 'production';
};

// Configuração do Sentry - Múltiplas fontes seguras (prioridade)
const getSentryDSN = (): string | undefined => {
  // 1. Runtime config (injetado pelo CI/CD) - PRIORIDADE MÁXIMA
  if (window.__RUNTIME_CONFIG__?.sentry?.dsn) {
    return window.__RUNTIME_CONFIG__.sentry.dsn;
  }
  
  // 2. App config (injetado pelo CI/CD) - SEGUNDA PRIORIDADE
  if (window.__APP_CONFIG__?.sentryDsn) {
    return window.__APP_CONFIG__.sentryDsn;
  }
  
  // 3. Global DSN (injetado pelo CI/CD) - TERCEIRA PRIORIDADE
  if (window.__SENTRY_DSN__) {
    return window.__SENTRY_DSN__;
  }
  
  // 4. Environment variable - APENAS PARA DESENVOLVIMENTO
  if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_SENTRY_DSN) {
    return process.env.REACT_APP_SENTRY_DSN;
  }
  
  return undefined;
};

class AnalyticsService {
  private isInitialized = false;
  private userConsent = false;

  // Verificar consentimento do usuário
  checkUserConsent(): boolean {
    // Verificar se o usuário deu consentimento para monitoramento
    try {
      if (typeof window === 'undefined' || !('localStorage' in window)) {
        return false;
      }
      const consent = localStorage.getItem('sentry_consent');
      return consent === 'true';
    } catch (e) {
      // Em ambientes onde o acesso ao localStorage é restrito (navegação privada, bloqueios de privacidade),
      // evitamos quebrar a aplicação e assumimos que não há consentimento ainda.
      console.warn('[Analytics] Não foi possível acessar localStorage ao verificar consentimento:', e);
      return false;
    }
  }

  // Definir consentimento do usuário
  setUserConsent(consent: boolean) {
    this.userConsent = consent;
    try {
      if (typeof window !== 'undefined' && ('localStorage' in window)) {
        localStorage.setItem('sentry_consent', consent.toString());
      }
    } catch (e) {
      // Apenas logar em console; não devemos impedir o fluxo.
      console.warn('[Analytics] Não foi possível persistir consentimento no localStorage:', e);
    }
    
    if (consent) {
      this.initialize();
    }
  }

  // Inicializar serviços de analytics
  initialize() {
    if (this.isInitialized) return;
    
    // Verificar consentimento antes de inicializar
    if (!this.checkUserConsent()) {
      console.log('Analytics: Aguardando consentimento do usuário');
      return;
    }

    try {
      const environment = getEnvironment();
      console.log(`🔥 Inicializando Firebase Analytics para ${environment.toUpperCase()}`);

      // Inicializar Sentry
      const sentryDsn = getSentryDSN();
      if (sentryDsn) {
        const sentryEnvironment = window.__RUNTIME_CONFIG__?.sentry?.environment || 
                                 window.__APP_CONFIG__?.environment || 
                                 environment;
                           
        Sentry.init({
          dsn: sentryDsn,
          environment: sentryEnvironment,
          tracesSampleRate: environment === 'production' ? 0.1 : 1.0,
          profilesSampleRate: environment === 'production' ? 0.1 : 1.0,
          replaysSessionSampleRate: environment === 'production' ? 0.01 : 0.1,
          replaysOnErrorSampleRate: 1.0,
          enableLogs: true,
          integrations: [
            Sentry.replayIntegration({
              maskAllText: true, // Mascarar todo texto para privacidade
              blockAllMedia: true, // Bloquear mídia para privacidade
              maskAllInputs: true, // Mascarar inputs sensíveis
            }),
            Sentry.browserTracingIntegration(),
            Sentry.consoleLoggingIntegration({ levels: ["log", "warn", "error"] })
          ],
          beforeSend(event) {
            // Filtrar dados sensíveis para conformidade com LGPD
            if (event.user) {
              // Remover TODOS os dados pessoais do usuário
              delete event.user.email;
              delete event.user.username;
              delete event.user.ip_address;
              delete event.user.name;
              delete event.user.phone;
              delete event.user.mobile;
              delete event.user.telefone;
              delete event.user.address;
              delete event.user.endereco;
              delete event.user.cpf;
              delete event.user.cnpj;
              delete event.user.rg;
              delete event.user.document;
              delete event.user.documento;
              delete event.user.birth_date;
              delete event.user.data_nascimento;
              delete event.user.date_of_birth;
              delete event.user.birthday;
              
              // Manter apenas ID e tipo de usuário
              event.user = {
                id: event.user.id,
                userType: event.user.userType
              };
            }
            
            // Filtrar breadcrumbs com dados sensíveis
            if (event.breadcrumbs) {
              event.breadcrumbs = event.breadcrumbs.map(breadcrumb => {
                if (breadcrumb.message) {
                  // Aplicar mascaramento completo usando utilitário
                  breadcrumb.message = sanitizeLogMessage(breadcrumb.message);
                }
                
                // Filtrar dados do breadcrumb
                if (breadcrumb.data) {
                  breadcrumb.data = createSafeLogData(breadcrumb.data);
                }
                
                return breadcrumb;
              });
            }
            
            // Filtrar contexto com dados sensíveis
            if (event.contexts) {
              Object.keys(event.contexts).forEach(key => {
                if (typeof event.contexts![key] === 'object' && event.contexts![key] !== null) {
                  event.contexts![key] = createSafeLogData(event.contexts![key]);
                }
              });
            }
            
            // Filtrar extra data
            if (event.extra) {
              event.extra = createSafeLogData(event.extra);
            }
            
            // Filtrar tags (remover tags que podem conter PII)
            if (event.tags) {
              const safeTags: Record<string, string> = {};
              Object.keys(event.tags).forEach(key => {
                const lowerKey = key.toLowerCase();
                // Manter apenas tags técnicas, remover tags com PII
                if (!lowerKey.includes('email') && 
                    !lowerKey.includes('phone') && 
                    !lowerKey.includes('name') && 
                    !lowerKey.includes('address') &&
                    !lowerKey.includes('cpf') &&
                    !lowerKey.includes('cnpj')) {
                  safeTags[key] = String(event.tags![key] || '');
                }
              });
              event.tags = safeTags;
            }
            
            // Filtrar exception messages
            if (event.exception) {
              event.exception.values?.forEach(exception => {
                if (exception.value) {
                  exception.value = sanitizeLogMessage(exception.value);
                }
              });
            }
            
            // Filtrar erros não importantes em produção
            if (process.env.NODE_ENV === 'production') {
              if (event.exception) {
                const error = event.exception.values?.[0];
                if (error?.value?.includes('Non-Error promise rejection') ||
                    error?.value?.includes('ResizeObserver loop limit exceeded') ||
                    error?.value?.includes('Script error') ||
                    error?.value?.includes('Sessão expirada. Faça login novamente.') ||
                    error?.type === 'SessionExpiredError') {
                  return null;
                }
              }
            }
            
            // Filtrar SessionExpiredError em todos os ambientes (comportamento esperado)
            if (event.exception) {
              const error = event.exception.values?.[0];
              if (error?.type === 'SessionExpiredError' || 
                  error?.value?.includes('SessionExpiredError')) {
                return null;
              }
            }
            
            return event;
          },
          beforeSendTransaction(event) {
            // Filtrar transações desnecessárias
            if (event.transaction === 'pageload' && 
                typeof event.tags?.url === 'string' && 
                event.tags.url.includes('chrome-extension://')) {
              return null;
            }
            
            // Filtrar PII das transações
            if (event.tags) {
              const safeTags: Record<string, string> = {};
              Object.keys(event.tags).forEach(key => {
                const lowerKey = key.toLowerCase();
                // Manter apenas tags técnicas, remover tags com PII
                if (!lowerKey.includes('email') && 
                    !lowerKey.includes('phone') && 
                    !lowerKey.includes('name') && 
                    !lowerKey.includes('address') &&
                    !lowerKey.includes('cpf') &&
                    !lowerKey.includes('cnpj') &&
                    !lowerKey.includes('user') &&
                    !lowerKey.includes('profile')) {
                  safeTags[key] = String(event.tags![key] || '');
                }
              });
              event.tags = safeTags;
            }
            
            // Filtrar dados extras das transações
            if (event.contexts) {
              Object.keys(event.contexts).forEach(key => {
                if (typeof event.contexts![key] === 'object' && event.contexts![key] !== null) {
                  event.contexts![key] = createSafeLogData(event.contexts![key]);
                }
              });
            }
            
            return event;
          }
        });
        console.log('Sentry inicializado');
      }

      // Inicializar Firebase Analytics
      try {
        initializeFirebaseAnalytics();
      } catch (error) {
        console.error('Erro ao inicializar Firebase Analytics:', error);
      }

      this.isInitialized = true;
    } catch (error) {
      console.error('Erro ao inicializar analytics:', error);
    }
  }

  // Rastrear visualização de página
  trackPageView(path: string, title?: string) {
    try {
      // Firebase Analytics
      firebaseAnalytics.logPageView(path, title);
    } catch (error) {
      console.error('Erro ao rastrear page view:', error);
    }
  }

  // Rastrear eventos personalizados
  trackEvent(action: string, category: string, label?: string, value?: number) {
    try {
      // Firebase Analytics
      firebaseAnalytics.logEvent(`${category}_${action}`, {
        category,
        label,
        value
      });
    } catch (error) {
      console.error('Erro ao rastrear evento:', error);
    }
  }

  // Rastrear ações específicas do Emprega Mecânico
  trackJobView(jobId: string, jobTitle?: string, companyName?: string) {
    firebaseAnalytics.logJobView(jobId, jobTitle || '', companyName || '');
  }

  trackJobApply(jobId: string, jobTitle?: string, companyName?: string) {
    firebaseAnalytics.logJobApplication(jobId, jobTitle || '', companyName || '');
  }

  trackSearch(searchTerm: string, category?: string) {
    firebaseAnalytics.logSearch(searchTerm, category);
  }

  trackFilter(filterType: string, filterValue: string) {
    firebaseAnalytics.logEvent('filter', {
      filter_type: filterType,
      filter_value: filterValue
    });
  }

  trackUserSignup(userType: 'candidate' | 'company') {
    firebaseAnalytics.logSignUp('email');
    firebaseAnalytics.setUserProperties({ user_type: userType });
  }

  trackUserLogin(userType: 'candidate' | 'company') {
    firebaseAnalytics.logLogin('email');
    firebaseAnalytics.setUserProperties({ user_type: userType });
  }

  trackFormSubmission(formName: string) {
    firebaseAnalytics.logEvent('form_submit', {
      form_name: formName
    });
  }

  trackError(error: Error, context?: string) {
    try {
      // Enviar para Sentry
      Sentry.captureException(error, {
        tags: {
          context: context || 'unknown'
        }
      });

      // Rastrear no Firebase Analytics
      firebaseAnalytics.logError(error.message, context);
    } catch (e) {
      console.error('Erro ao rastrear erro:', e);
    }
  }

  // Definir informações do usuário (sem PII)
  setUser(userId: string, userType: 'candidate' | 'company') {
    try {
      // Sentry - apenas ID e tipo, sem dados pessoais
      Sentry.setUser({
        id: userId,
        userType
      });

      // Firebase Analytics - apenas ID e tipo
      firebaseAnalytics.setUserId(userId);
      firebaseAnalytics.setUserProperties({
        user_type: userType
      });
    } catch (error) {
      console.error('Erro ao definir usuário:', error);
    }
  }

  // Rastrear login
  trackLogin(method: string = 'email') {
    firebaseAnalytics.logLogin(method);
  }

  // Rastrear logout
  trackLogout() {
    firebaseAnalytics.logLogout();
    firebaseAnalytics.clearUserData();
  }

  // Rastrear cadastro
  trackSignUp(method: string = 'email') {
    firebaseAnalytics.logSignUp(method);
  }

  // Rastrear performance
  trackTiming(category: string, variable: string, value: number, label?: string) {
    try {
      firebaseAnalytics.logEvent('timing_complete', {
        name: variable,
        value: value,
        category: category,
        label: label
      });
    } catch (error) {
      console.error('Erro ao rastrear timing:', error);
    }
  }

  // Capturar breadcrumb personalizado
  addBreadcrumb(message: string, category: string, level: 'info' | 'warning' | 'error' = 'info') {
    try {
      Sentry.addBreadcrumb({
        message,
        category,
        level
      });
    } catch (error) {
      console.error('Erro ao adicionar breadcrumb:', error);
    }
  }

  // Monitoramento de performance
  measurePerformance() {
    try {
      // Web Vitals
      import('web-vitals').then(({ getCLS, getFID, getFCP, getLCP, getTTFB }) => {
        getCLS(this.sendToAnalytics);
        getFID(this.sendToAnalytics);
        getFCP(this.sendToAnalytics);
        getLCP(this.sendToAnalytics);
        getTTFB(this.sendToAnalytics);
      }).catch((error: Error) => {
        console.log('Web-vitals não disponível:', error.message);
      });

      // Performance API
      if (performance && performance.getEntriesByType) {
        // Tempo de carregamento da página
        const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
        if (navigation) {
          this.trackTiming('page_load', 'dom_content_loaded', navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart);
          this.trackTiming('page_load', 'load_complete', navigation.loadEventEnd - navigation.loadEventStart);
        }

        // Recursos carregados
        const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
        resources.forEach(resource => {
          if (resource.duration > 1000) { // Recursos que demoraram mais de 1s
            this.trackTiming('resource_load', resource.name.split('/').pop() || 'unknown', resource.duration);
          }
        });
      }
    } catch (error) {
      console.error('Erro ao medir performance:', error);
    }
  }

  private sendToAnalytics = (metric: any) => {
    try {
      const { name, value } = metric;
      this.trackTiming('web_vitals', name, Math.round(value));
      
      // Enviar para Sentry também
      Sentry.addBreadcrumb({
        message: `Web Vital: ${name} = ${Math.round(value)}`,
        category: 'performance',
        level: 'info'
      });
    } catch (error) {
      console.error('Erro ao enviar métrica:', error);
    }
  }

  // Rastrear tempo gasto em páginas
  startPageTimer(pageName: string) {
    try {
      (window as any)._pageStartTime = Date.now();
      (window as any)._currentPage = pageName;
    } catch (error) {
      console.error('Erro ao iniciar timer da página:', error);
    }
  }

  endPageTimer() {
    try {
      const startTime = (window as any)._pageStartTime;
      const pageName = (window as any)._currentPage;
      
      if (startTime && pageName) {
        const timeSpent = Date.now() - startTime;
        this.trackTiming('page_engagement', pageName, timeSpent);
      }
    } catch (error) {
      console.error('Erro ao finalizar timer da página:', error);
    }
  }
}

// Instância singleton
export const analytics = new AnalyticsService();

// Logger seguro que remove PII automaticamente
export const logger = {
  // Log de debug - apenas em desenvolvimento
  debug: (message: string, data?: any) => {
    const safeMessage = sanitizeLogMessage(message);
    const safeData = createSafeLogData(data);
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`[DEBUG] ${safeMessage}`, safeData);
    }
    Sentry.logger.debug(safeMessage, safeData);
  },

  // Log de informação
  info: (message: string, data?: any) => {
    const safeMessage = sanitizeLogMessage(message);
    const safeData = createSafeLogData(data);
    
    console.log(`[INFO] ${safeMessage}`, safeData);
    Sentry.logger.info(safeMessage, safeData);
  },

  // Log de aviso
  warn: (message: string, data?: any) => {
    const safeMessage = sanitizeLogMessage(message);
    const safeData = createSafeLogData(data);
    
    console.warn(`[WARN] ${safeMessage}`, safeData);
    Sentry.logger.warn(safeMessage, safeData);
  },

  // Log de erro
  error: (message: string, error?: Error, data?: any) => {
    const safeMessage = sanitizeLogMessage(message);
    const skipSentry = data?.skipSentry === true;
    // Remover skipSentry dos dados antes de processar
    const dataWithoutSkipSentry = data ? { ...data } : undefined;
    if (dataWithoutSkipSentry && 'skipSentry' in dataWithoutSkipSentry) {
      delete dataWithoutSkipSentry.skipSentry;
    }
    const safeData = createSafeLogData(dataWithoutSkipSentry);
    
    console.error(`[ERROR] ${safeMessage}`, error, safeData);
    
    // Não reportar ao Sentry se for SessionExpiredError (comportamento esperado)
    // ou se skipSentry estiver definido
    if (error && isSessionExpiredError(error)) {
      // Apenas adicionar breadcrumb ao invés de exception
      // Sessão expirada é um comportamento esperado, não um erro crítico
      Sentry.addBreadcrumb({
        message: safeMessage,
        level: 'info',
        category: 'auth',
        data: {
          ...safeData,
          reason: (error as SessionExpiredError).reason,
          isSessionExpired: true
        }
      });
      return;
    }
    
    // Não reportar ao Sentry se skipSentry estiver definido
    // Útil para erros temporários esperados (ex: erros de rede durante refresh de token)
    if (skipSentry) {
      // Apenas adicionar breadcrumb ao invés de exception
      Sentry.addBreadcrumb({
        message: safeMessage,
        level: 'warning',
        category: 'network_error',
        data: safeData
      });
      return;
    }
    
    if (error) {
      Sentry.captureException(error, {
        tags: {
          custom_error: true,
          error_message: safeMessage
        },
        extra: safeData ? { ...safeData } : undefined
      });
    } else {
      Sentry.logger.error(safeMessage, safeData);
    }
  },

  // Log específico para autenticação
  auth: (message: string, data?: any) => {
    const safeMessage = sanitizeLogMessage(message);
    const safeData = createAuthLogData(data);
    
    console.log(`[AUTH] ${safeMessage}`, safeData);
    Sentry.logger.info(safeMessage, { ...safeData, category: 'auth' });
  },

  // Log específico para API
  api: (message: string, data?: any) => {
    const safeMessage = sanitizeLogMessage(message);
    const safeData = createApiLogData(data);
    
    console.log(`[API] ${safeMessage}`, safeData);
    Sentry.logger.info(safeMessage, { ...safeData, category: 'api' });
  }
};

// HOC para capturar erros de componentes React
export const withErrorBoundary = (Component: React.ComponentType<any>) => {
  return Sentry.withErrorBoundary(Component, {
    fallback: ({ error, resetError, eventId }) => (
      <div style={{ 
        padding: '40px 20px', 
        textAlign: 'center', 
        backgroundColor: '#f8f9fa',
        border: '1px solid #dee2e6',
        borderRadius: '8px',
        fontFamily: 'Arial, sans-serif',
        maxWidth: '600px',
        margin: '20px auto'
      }}>
        <div style={{ fontSize: '48px', marginBottom: '20px' }}>⚠️</div>
        <h2 style={{ color: '#105099', marginBottom: '16px' }}>
          Ops! Algo deu errado
        </h2>
        <p style={{ color: '#666', marginBottom: '24px', fontSize: '16px' }}>
          Ocorreu um erro inesperado em nossa aplicação. Nossa equipe técnica foi notificada automaticamente.
        </p>
        
        {eventId && (
          <p style={{ 
            fontSize: '12px', 
            color: '#999', 
            backgroundColor: '#e9ecef',
            padding: '8px',
            borderRadius: '4px',
            marginBottom: '20px',
            fontFamily: 'monospace'
          }}>
            ID do erro: {eventId}
          </p>
        )}
        
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button 
            onClick={resetError}
            style={{
              backgroundColor: '#105099',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            Tentar novamente
          </button>
          
          <button 
            onClick={() => window.location.href = '/'}
            style={{
              backgroundColor: 'transparent',
              color: '#105099',
              border: '1px solid #105099',
              padding: '12px 24px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            Ir para página inicial
          </button>
        </div>
        
        <details style={{ 
          marginTop: '24px', 
          textAlign: 'left',
          backgroundColor: '#fff',
          border: '1px solid #dee2e6',
          borderRadius: '4px',
          padding: '16px'
        }}>
          <summary style={{ 
            cursor: 'pointer', 
            fontWeight: '500',
            color: '#105099',
            marginBottom: '12px'
          }}>
            Detalhes técnicos (para desenvolvedores)
          </summary>
          <pre style={{ 
            fontSize: '12px', 
            color: '#666',
            backgroundColor: '#f8f9fa',
            padding: '12px',
            borderRadius: '4px',
            overflow: 'auto',
            whiteSpace: 'pre-wrap'
          }}>
            {error instanceof Error ? error.message : String(error)}
          </pre>
        </details>
      </div>
    ),
    beforeCapture: (scope, error, errorInfo) => {
      // Adicionar contexto adicional antes de capturar o erro
      scope.setTag('errorBoundary', true);
      if (errorInfo && typeof errorInfo === 'object') {
        scope.setContext('errorInfo', {
          componentStack: (errorInfo as any).componentStack || 'unknown',
          errorBoundary: (errorInfo as any).errorBoundary || 'unknown'
        });
      }
    }
  });
};

export default analytics;
